import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import { type CreatePayloadSchema, UpdatePayloadSchema } from '../types/types'
import { useAuth } from './useAuth'
import { throwOnError } from '../utils/throwOnError'

export const useGetPayloadSchemasQuery = (labId: number | null | undefined) => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['payload-schemas', labId],
    enabled: labId !== undefined,
    queryFn: async () => {
      const { data, error } = await authFetch().GET('/payload-schemas', {
        params: {
          query: {
            labId: labId ?? undefined
          }
        }
      })
      if (error) {
        throwOnError(error)
      }
      return data
    }
  })
}

export const useGetPayloadSchemaByIdQuery = (payloadSchemaId?: number) => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['payload-schemas', payloadSchemaId],
    queryFn: payloadSchemaId
      ? async () => {
          const { data, error } = await authFetch().GET(
            '/payload-schemas/{payloadSchemaId}',
            {
              params: {
                path: {
                  payloadSchemaId
                }
              }
            }
          )
          if (error) {
            throwOnError(error)
          }
          return data
        }
      : skipToken
  })
}

export const useCreatePayloadSchemaMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async (payloadSchema: CreatePayloadSchema) => {
      const { data, error } = await authFetch().POST('/payload-schemas', {
        body: payloadSchema
      })
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['payload-schemas']
      })
    }
  })
}

interface UpdatePayloadSchemaMutationVariables {
  payloadSchemaId: number
  etag: string
  body: UpdatePayloadSchema
}

export const useUpdatePayloadSchemaMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({
      payloadSchemaId,
      etag,
      body
    }: UpdatePayloadSchemaMutationVariables) => {
      const { data, error } = await authFetch().PUT(
        '/payload-schemas/{payloadSchemaId}',
        {
          params: {
            header: {
              'If-Match': etag
            },
            path: { payloadSchemaId }
          },
          body
        }
      )
      if (error) {
        throwOnError(error)
      }
      return data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['payload-schemas']
      })
    }
  })
}

export const useDeletePayloadSchemaMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async (payloadSchemaId: number) =>
      await authFetch().DELETE('/payload-schemas/{payloadSchemaId}', {
        params: {
          path: { payloadSchemaId }
        }
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['payload-schemas']
      })
    }
  })
}
